import React from "react";
import { useLanguage } from "../context/LanguageContext";
import { useTheme } from "../context/ThemeContext";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const PrivacyPolicy = () => {
  const { language } = useLanguage();
  const { theme } = useTheme();
  const isDark = theme === "dark";

  return (
    <section className="py-32 px-8">
      {/* Return Button */}
      <Link
        to="/"
        className={`
          fixed
          top-24
          left-8
          flex
          items-center
          gap-2
          px-4
          py-2
          rounded-lg
          transition-all
          duration-300
          border
          ${isDark 
            ? "text-gray-300 border-green-500/30 hover:border-green-400 hover:text-green-400 hover:bg-green-500/10" 
            : "text-gray-700 border-green-900/30 hover:border-green-800 hover:text-green-900 hover:bg-green-900/10"
          }
        `}
      >
        <ArrowLeft className="w-4 h-4" />
        {language === "en" ? "Back to Home" : "Retour à l'Accueil"}
      </Link>

      <div className="max-w-4xl mx-auto">
        {/* Title */}
        <h1 className={`
          text-4xl md:text-5xl lg:text-6xl 
          font-bold 
          text-center
          ${isDark 
            ? "bg-gradient-to-r from-green-400 via-green-500 to-green-400" 
            : "bg-gradient-to-r from-green-900 via-green-800 to-green-900"
          }
          bg-clip-text 
          text-transparent
          mb-16
        `}>
          {language === "en" ? "Privacy Policy" : "Politique de Confidentialité"}
        </h1>

        {/* Content Container */}
        <div className={`
          prose max-w-none
          ${isDark ? "prose-invert" : ""}
          ${isDark ? "text-gray-300" : "text-gray-700"}
        `}>
          {/* Last Updated */}
          <p className="text-sm mb-8">
            {language === "en" 
              ? "Last Updated: April 2024" 
              : "Dernière mise à jour : Avril 2024"}
          </p>

          {/* 1. Introduction */}
          <h2>
            {language === "en" 
              ? "1. Introduction" 
              : "1. Introduction"}
          </h2>
          <p>
            {language === "en"
              ? 'Dynovant Systems ("we," "us," or "our") is committed to protecting the privacy of our clients, partners, and website visitors ("you"). This Privacy Policy explains how we collect, use, disclose, and safeguard your personal information when you interact with our digital services, including our website (dynovant.com) and any services we provide.'
              : 'Dynovant Systems (« nous » ou « notre ») s\'engage à protéger la confidentialité de nos clients, partenaires et visiteurs du site web (« vous »). Cette Politique de Confidentialité explique comment nous collectons, utilisons, divulguons et protégeons vos informations personnelles lorsque vous interagissez avec nos services numériques, y compris notre site web (dynovant.com) et tous les services que nous fournissons.'}
          </p>

          {/* 2. Information We Collect */}
          <h2>
            {language === "en" 
              ? "2. Information We Collect" 
              : "2. Informations que Nous Collectons"}
          </h2>
          <h3>
            {language === "en" 
              ? "Personal Information:" 
              : "Informations Personnelles :"}
          </h3>
          <ul>
            <li>
              {language === "en"
                ? "Contact details (name, email address, phone number, etc.) provided voluntarily via contact forms or registration."
                : "Coordonnées (nom, adresse e-mail, numéro de téléphone, etc.) fournies volontairement via les formulaires de contact ou l'inscription."}
            </li>
            <li>
              {language === "en"
                ? "Business information relevant to service inquiries, such as company name and job title."
                : "Informations professionnelles pertinentes pour les demandes de service, telles que le nom de l'entreprise et le titre du poste."}
            </li>
          </ul>
          <h3>
            {language === "en" 
              ? "Usage Data:" 
              : "Données d'Utilisation :"}
          </h3>
          <ul>
            <li>
              {language === "en"
                ? "Information automatically collected when you visit our website (IP address, browser type, operating system, pages visited, and referring URLs)."
                : "Informations collectées automatiquement lors de votre visite sur notre site web (adresse IP, type de navigateur, système d'exploitation, pages visitées et URLs de référence)."}
            </li>
            <li>
              {language === "en"
                ? "Data collected via cookies and similar tracking technologies to enhance and personalize your experience."
                : "Données collectées via des cookies et technologies de suivi similaires pour améliorer et personnaliser votre expérience."}
            </li>
          </ul>
          <h3>
            {language === "en" 
              ? "Technical Data:" 
              : "Données Techniques :"}
          </h3>
          <ul>
            <li>
              {language === "en"
                ? "Details regarding your device, including device type and unique device identifiers, as applicable."
                : "Détails concernant votre appareil, y compris le type d'appareil et les identifiants uniques d'appareil, le cas échéant."}
            </li>
          </ul>

          {/* 3. How We Use Your Information */}
          <h2>
            {language === "en" 
              ? "3. How We Use Your Information" 
              : "3. Comment Nous Utilisons Vos Informations"}
          </h2>
          <p>
            {language === "en"
              ? "We may use your information for the following purposes:"
              : "Nous pouvons utiliser vos informations aux fins suivantes :"}
          </p>
          <ul>
            <li>
              {language === "en"
                ? "Providing Services: To deliver and maintain our digital, automation, AI, and integration services."
                : "Fourniture de Services : Pour fournir et maintenir nos services numériques, d'automatisation, d'IA et d'intégration."}
            </li>
            <li>
              {language === "en"
                ? "Communication: To respond to inquiries, send service-related communications, and provide updates."
                : "Communication : Pour répondre aux demandes, envoyer des communications liées aux services et fournir des mises à jour."}
            </li>
            <li>
              {language === "en"
                ? "Improvement: To analyze usage patterns and improve our website, services, and customer experience."
                : "Amélioration : Pour analyser les modèles d'utilisation et améliorer notre site web, nos services et l'expérience client."}
            </li>
            <li>
              {language === "en"
                ? "Marketing: To inform you about offers, new features, and other information that may be of interest (with your consent where required)."
                : "Marketing : Pour vous informer des offres, nouvelles fonctionnalités et autres informations qui pourraient vous intéresser (avec votre consentement lorsque requis)."}
            </li>
          </ul>

          {/* 4. Disclosure of Your Information */}
          <h2>
            {language === "en" 
              ? "4. Disclosure of Your Information" 
              : "4. Divulgation de Vos Informations"}
          </h2>
          <p>
            {language === "en"
              ? "We will not sell or rent your personal data to third parties. We may share your information with:"
              : "Nous ne vendrons ni ne louerons vos données personnelles à des tiers. Nous pouvons partager vos informations avec :"}
          </p>
          <ul>
            <li>
              {language === "en"
                ? "Service Providers: Trusted third-party vendors who assist us in delivering our services, under contractual obligations to protect your information."
                : "Prestataires de Services : Des fournisseurs tiers de confiance qui nous aident à fournir nos services, sous obligations contractuelles de protéger vos informations."}
            </li>
            <li>
              {language === "en"
                ? "Legal Compliance: Authorities or third parties if required by law or in connection with legal proceedings."
                : "Conformité Légale : Les autorités ou tiers si requis par la loi ou dans le cadre de procédures judiciaires."}
            </li>
            <li>
              {language === "en"
                ? "Business Transfers: In connection with a merger, acquisition, or sale of assets, provided that the transferee agrees to respect this Privacy Policy."
                : "Transferts d'Entreprise : Dans le cadre d'une fusion, acquisition ou vente d'actifs, à condition que le cessionnaire accepte de respecter cette Politique de Confidentialité."}
            </li>
          </ul>

          {/* 5. Data Security */}
          <h2>
            {language === "en" 
              ? "5. Data Security" 
              : "5. Sécurité des Données"}
          </h2>
          <p>
            {language === "en"
              ? "We implement reasonable administrative, technical, and physical safeguards designed to protect your personal information from unauthorized access, disclosure, alteration, or destruction. However, no method of transmission over the Internet or electronic storage is 100% secure."
              : "Nous mettons en œuvre des mesures de protection administratives, techniques et physiques raisonnables conçues pour protéger vos informations personnelles contre l'accès, la divulgation, l'altération ou la destruction non autorisés. Cependant, aucune méthode de transmission sur Internet ou de stockage électronique n'est sécurisée à 100%."}
          </p>

          {/* 6. Cookies and Tracking Technologies */}
          <h2>
            {language === "en" 
              ? "6. Cookies and Tracking Technologies" 
              : "6. Cookies et Technologies de Suivi"}
          </h2>
          <p>
            {language === "en"
              ? "Our website uses cookies and similar tracking technologies to:"
              : "Notre site web utilise des cookies et des technologies de suivi similaires pour :"}
          </p>
          <ul>
            <li>
              {language === "en"
                ? "Enhance website performance and user experience."
                : "Améliorer les performances du site web et l'expérience utilisateur."}
            </li>
            <li>
              {language === "en"
                ? "Collect information on website usage for analytics and improvement."
                : "Collecter des informations sur l'utilisation du site web pour l'analyse et l'amélioration."}
            </li>
          </ul>
          <p>
            {language === "en"
              ? "You may adjust your browser settings to refuse cookies; however, this may affect your ability to use certain features of our website."
              : "Vous pouvez ajuster les paramètres de votre navigateur pour refuser les cookies ; cependant, cela peut affecter votre capacité à utiliser certaines fonctionnalités de notre site web."}
          </p>

          {/* 7. Data Retention */}
          <h2>
            {language === "en" 
              ? "7. Data Retention" 
              : "7. Conservation des Données"}
          </h2>
          <p>
            {language === "en"
              ? "We will retain your personal information for as long as necessary to fulfill the purposes outlined in this Privacy Policy unless a longer retention period is required or permitted by law."
              : "Nous conserverons vos informations personnelles aussi longtemps que nécessaire pour atteindre les objectifs décrits dans cette Politique de Confidentialité, sauf si une période de conservation plus longue est requise ou autorisée par la loi."}
          </p>

          {/* 8. Your Rights */}
          <h2>
            {language === "en" 
              ? "8. Your Rights" 
              : "8. Vos Droits"}
          </h2>
          <p>
            {language === "en"
              ? "Depending on your jurisdiction, you may have rights regarding your personal data, including:"
              : "Selon votre juridiction, vous pouvez avoir des droits concernant vos données personnelles, notamment :"}
          </p>
          <ul>
            <li>
              {language === "en"
                ? "The right to access, correct, or delete your information."
                : "Le droit d'accéder à vos informations, de les corriger ou de les supprimer."}
            </li>
            <li>
              {language === "en"
                ? "The right to object to or restrict certain processing activities."
                : "Le droit de vous opposer à certaines activités de traitement ou de les restreindre."}
            </li>
            <li>
              {language === "en"
                ? "The right to data portability."
                : "Le droit à la portabilité des données."}
            </li>
          </ul>
          <p>
            {language === "en"
              ? "To exercise these rights, please contact us using the information provided below."
              : "Pour exercer ces droits, veuillez nous contacter en utilisant les informations fournies ci-dessous."}
          </p>

          {/* 9. Third-Party Links */}
          <h2>
            {language === "en" 
              ? "9. Third-Party Links" 
              : "9. Liens Tiers"}
          </h2>
          <p>
            {language === "en"
              ? "Our website may contain links to third-party websites. We are not responsible for the privacy practices or content of these sites. We encourage you to read the privacy policies of any linked websites."
              : "Notre site web peut contenir des liens vers des sites web tiers. Nous ne sommes pas responsables des pratiques de confidentialité ou du contenu de ces sites. Nous vous encourageons à lire les politiques de confidentialité de tout site web lié."}
          </p>

          {/* 10. Changes to This Privacy Policy */}
          <h2>
            {language === "en" 
              ? "10. Changes to This Privacy Policy" 
              : "10. Modifications de cette Politique de Confidentialité"}
          </h2>
          <p>
            {language === "en"
              ? "We reserve the right to update or modify this Privacy Policy at any time. Changes will be posted on our website with an updated effective date. Your continued use of our services constitutes acceptance of the updated policy."
              : "Nous nous réservons le droit de mettre à jour ou de modifier cette Politique de Confidentialité à tout moment. Les modifications seront publiées sur notre site web avec une date d'effet mise à jour. Votre utilisation continue de nos services constitue l'acceptation de la politique mise à jour."}
          </p>

          {/* 11. Contact Us */}
          <h2>
            {language === "en" 
              ? "11. Contact Us" 
              : "11. Nous Contacter"}
          </h2>
          <p>
            {language === "en"
              ? "If you have any questions or concerns regarding this Privacy Policy, please contact us at:"
              : "Si vous avez des questions ou des préoccupations concernant cette Politique de Confidentialité, veuillez nous contacter à :"}
          </p>
          <p>
            📧 Email: contact@dynovant.com<br />
            📍 {language === "en" 
              ? "Address: Dubai, UAE / Paris, France" 
              : "Adresse : Dubai, EAU / Paris, France"}<br />
            📱 {language === "en" 
              ? "Phone: +971 58 514 5940" 
              : "Téléphone : +971 58 514 5940"}
          </p>
        </div>
      </div>
    </section>
  );
};

export default PrivacyPolicy; 